import React, { FC, useCallback, useMemo, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { type TFunction } from 'i18next';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { StringParam, useQueryParams } from 'use-query-params';
import { Schema } from 'yup';

import { FolderType, UpdateFolderRequestBody, useGetFolder, useUpdateFolder } from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import { useFolderPermissions } from '@/hooks/permissions/useFolderPermissions';
import { ROUTER_PATHS, SETTINGS_STEP_KEYS, toOrganizationItemSettings } from '@/root/linker';
import SettingsShell, { SettingsShellRef } from '@/views/SettingsShell/SettingsShell';

import { getFolderBasicInfoValidationSchema, getRolesMembersValidationSchema } from './schema';
import { FolderData, FolderFieldValues } from './types';

const FOLDER_KEYS_BY_TAB = {
  [SETTINGS_STEP_KEYS.BASIC_INFO]: ['status', 'name', 'legacyId'],
  [SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS]: ['admins', 'organizationAdmins', 'userInviteEmailLanguage']
} as { [key: string]: string[] };

const ALL_FOLDER_KEYS = Object.values(FOLDER_KEYS_BY_TAB).flatMap(list => list);
const OMIT_ON_SAVE_KEYS = ['status'];

const TRANSFORMED_ON_LOAD_KEYS = ['admins'];

const presaveTransform = (fieldValues: UpdateFolderRequestBody, stepKey: SETTINGS_STEP_KEYS) => {
  const updateRequest = _pick(fieldValues, ALL_FOLDER_KEYS) as UpdateFolderRequestBody;

  return _omit(_pick(updateRequest, FOLDER_KEYS_BY_TAB[stepKey]), OMIT_ON_SAVE_KEYS);
};

const preloadTransform = (folder: FolderData) => {
  const fieldValues = _omit(_pick(folder, ALL_FOLDER_KEYS), TRANSFORMED_ON_LOAD_KEYS) as FolderFieldValues;

  fieldValues.admins = folder?.admins?.filter(admin => admin?.id)?.map(({ id }) => ({ id: id! }));

  return fieldValues;
};

const folderItemSettingsTabs = (
  t: TFunction,
  stepToLink: (step: SETTINGS_STEP_KEYS) => string,
  canViewBasicInfo?: boolean,
  canViewRolesAndUsers?: boolean
) =>
  [
    canViewBasicInfo && {
      stepKey: SETTINGS_STEP_KEYS.BASIC_INFO,
      label: t('orgSettings.tabs.basicInfo'),
      link: stepToLink(SETTINGS_STEP_KEYS.BASIC_INFO)
    },
    canViewRolesAndUsers && {
      stepKey: SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS,
      label: t('orgSettings.tabs.rolesAndUsers'),
      link: stepToLink(SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS)
    }
  ].filter((item): item is { stepKey: SETTINGS_STEP_KEYS; label: string; link: string } => Boolean(item));

const URL_CONFIG_MAP = {
  locationSlug: StringParam
};

const FolderSettings: FC = () => {
  const { spacing } = useTheme();
  const { t } = useTranslation(['settings', 'common', 'validation']);
  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);

  const folderPermissionVariables = useMemo(
    () => ({
      organizationSlug,
      locationSlug: locationSlug ?? undefined,
      locationType: FolderType.FOLDER
    }),
    [locationSlug, organizationSlug]
  );

  const {
    data: folder,
    isLoading: isFolderLoading,
    refetch: refetchFolder
  } = useGetFolder(organizationSlug, locationSlug!, {
    query: {
      enabled: !!organizationSlug && !!locationSlug
    }
  });
  const { mutateAsync: updateFolder, isLoading: isFolderUpdating } = useUpdateFolder();

  const formRef = useRef<SettingsShellRef<FieldValues>>(null);

  const { value: { canViewBasicInfo, canViewRolesAndUsers } = {}, isLoading: isLoadingFolderPermissions } =
    useFolderPermissions({
      variables: folderPermissionVariables
    });

  const isLoading = isFolderLoading || isFolderUpdating || isLoadingFolderPermissions;

  const saveFolderSettings = useCallback(
    async (data: FolderFieldValues, stepKey: SETTINGS_STEP_KEYS) => {
      await updateFolder({
        organizationSlug: organizationSlug,
        folderSlug: locationSlug!,
        data: presaveTransform(data, stepKey)
      });
      await refetchFolder();
    },
    [locationSlug, organizationSlug, refetchFolder, updateFolder]
  );

  const tabs = useMemo(
    () =>
      folderItemSettingsTabs(
        t,
        (stepKey: SETTINGS_STEP_KEYS) =>
          toOrganizationItemSettings({
            organizationSlug,
            locationSlug: locationSlug!,
            stepKey,
            locationType: FolderType.FOLDER
          }),
        canViewBasicInfo,
        canViewRolesAndUsers
      ),
    [locationSlug, organizationSlug, t, canViewBasicInfo, canViewRolesAndUsers]
  );

  const defaultValues = useMemo(() => folder && preloadTransform(folder as FolderData), [folder]);

  const validations = useMemo(
    () =>
      ({
        [SETTINGS_STEP_KEYS.BASIC_INFO]: getFolderBasicInfoValidationSchema(t),
        [SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS]: getRolesMembersValidationSchema(t)
      }) as { [key: string]: Schema<FolderFieldValues> },
    [t]
  );

  const defaultStepKey = useMemo(() => {
    if (canViewBasicInfo) return SETTINGS_STEP_KEYS.BASIC_INFO;
    if (canViewRolesAndUsers) return SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS;

    return SETTINGS_STEP_KEYS.BASIC_INFO;
  }, [canViewBasicInfo, canViewRolesAndUsers]);

  if (!locationSlug) {
    return null;
  }

  return (
    <>
      <SettingsShell
        ref={formRef}
        tabs={tabs}
        defaultValues={defaultValues!}
        defaultRoute={toOrganizationItemSettings({
          organizationSlug,
          stepKey: defaultStepKey,
          locationSlug: locationSlug!,
          locationType: FolderType.FOLDER
        })}
        routePattern={ROUTER_PATHS.FOLDER_SETTINGS_GENERIC}
        sx={{ px: 5, pb: 5, margin: spacing(2, 1) }}
        onSave={saveFolderSettings}
        validations={validations}
      >
        <Outlet />
      </SettingsShell>
      {isLoading && <BackdropLoader />}
    </>
  );
};

export default FolderSettings;
